import { BirthWishModel } from "../BirthWishModel";
import { BirthWishFormValues } from "../BirthWishFormValidation";
import { ContactModel } from "@/Domains/Contact";
import { DateFormatHelper, formatName, MessageHelper } from "@/Shared";


/**
 * =====================================================
 * Class: BirthWishFormViewModel
 * =====================================================
 *
 * Description:
 * ViewModel for managing the state of the BirthWish form.
 * It prepares initial values for form rendering, formats names and dates,
 * and provides helpers for form submission and display titles.
 *
 * Responsibilities:
 * - Map model data to form values
 * - Format contact names and birth dates for display
 * - Provide default messages for new birth wishes
 * - Determine whether the form is in creation or edit mode
 * 
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class BirthWishFormViewModel {
    public id!: number;
    public contactId!: number;
    public message!: string;
    public contactName!: string;
    public valid!: boolean;
    public birthDay!: string;

    /**
     * Constructor
     * @param _receiver Optional contact to prefill the form
     * @param _birthWish Optional existing birth wish to edit
     */
    public constructor(
        private _receiver?: ContactModel,
        private _birthWish?: BirthWishModel
    ) {

        // Initialize properties immediately
        this.initialize();
    }

    /**
     * Initializes the ViewModel properties based on input data
     */
    private initialize() {

        // Assign ID: 0 if creating a new wish
        this.id = this._birthWish?.id ?? 0;

        // Determine contact ID: from receiver if creating, or from existing wish
        this.contactId = this._receiver?.id ?? this._birthWish?.contactId ?? 0;

        // Format the contact's full name for display
        this.contactName = formatName(this._receiver?.person);

        // Set the message: use existing message if editing, else generate a new one
        this.message = this._birthWish
            ? (this._birthWish?.message ?? "")
            : MessageHelper.BIRTHWISH.NEW(this.contactName);

        // Format birth date for display
        this.birthDay = DateFormatHelper.toLocaleDateString(
            this._receiver?.person.birthDate
            ?? this._birthWish?.contact?.person.birthDate
        );
    }

     /**
     * Returns the values suitable for the Formik form
     */
    get formValues(): BirthWishFormValues {
        return {
            id: this.id,
            contactId: this.contactId,
            message: this.message,
            valid: this.valid
        } as BirthWishFormValues;
    }

    /**
     * Returns the title for the form card
     * - Edit mode: "Voeu N° {id} - {contactName}"
     * - Creation mode: "Création de Voeu pour {contactName}"
     */
    get title(): string {
        return this._birthWish
            ? `Voeu N° ${this.id} - ${this.contactName}`
            : `Création de Voeu pour ${this.contactName}`;
    }

    /**
     * Determines whether the form is in creation mode
     */
    isCreationMode(): boolean {
        return !this.id;
    }
}
